'use client';
import React, { useEffect, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/uiKit/FormControl';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Focus, Save, Trash2, Edit, Users, Database, Check, X, Calendar, Image, Video, Upload, AlertTriangle, Info, AlertCircle, Zap, EyeOff, Eye } from 'lucide-react';
import { useAuth } from '@/providers/AuthProvider';

interface CustomVerticalLineMenuProps {
  open: boolean;
  onClose: () => void;
  onFocusGroup: () => void;
  onUnfocusGroup?: () => void;
  onSaveToDatabase: () => void;
  onRemove?: () => void;
  onDim?: () => void; // Toggle dim state for single line
  onDimGroup?: () => void; // Toggle dim state for entire group
  onEdit?: (updates: { label?: string; description?: string; mediaType?: 'image' | 'video'; mediaUrl?: string; thumbnailUrl?: string; severity?: 'info' | 'low' | 'medium' | 'high' | 'critical'; editedAt?: string; editedBy?: string }) => void;
  position?: { x: number; y: number };
  lineData?: {
    id: string;
    label: string;
    groupId: string;
    source?: 'system' | 'user';
    username?: string;
    timestamp?: number;
    description?: string;
    mediaType?: 'image' | 'video';
    mediaUrl?: string;
    thumbnailUrl?: string;
    capturedData?: {
      xValue?: any;
      yValues?: Record<string, number>;
      timestamp?: any;
    };
    editedAt?: string;
    editedBy?: string;
    severity?: 'info' | 'low' | 'medium' | 'high' | 'critical';
    dimmed?: boolean;
    groupDimmed?: boolean; // Whether the entire group is dimmed
    deletable?: boolean;
  };
  isFocused?: boolean;
  canRemove?: boolean; // System lines cannot be removed
}

/**
 * Menu that appears when clicking on a custom vertical line icon
 * Provides Focus Group, Save to Database, and Remove options
 */
export const CustomVerticalLineMenu: React.FC<CustomVerticalLineMenuProps> = ({
  open,
  onClose,
  onFocusGroup,
  onUnfocusGroup,
  onSaveToDatabase,
  onRemove,
  onDim,
  onDimGroup,
  onEdit,
  position,
  lineData,
  isFocused = false,
  canRemove = true,
}) => {
  const menuRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const { user } = useAuth();
  
  // State for inline editing
  const [isEditing, setIsEditing] = useState(false);
  const [editingLabel, setEditingLabel] = useState('');
  const [editingDescription, setEditingDescription] = useState('');
  const [editingMediaType, setEditingMediaType] = useState<'image' | 'video'>('image');
  const [editingMediaUrl, setEditingMediaUrl] = useState('');
  const [editingThumbnailUrl, setEditingThumbnailUrl] = useState('');
  const [editingSeverity, setEditingSeverity] = useState<'info' | 'low' | 'medium' | 'high' | 'critical'>('info');
  
  // Focus input when entering edit mode
  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  // Handle starting edit mode
  const handleStartEdit = () => {
    if (lineData) {
      setEditingLabel(lineData.label || '');
      setEditingDescription(lineData.description || '');
      setEditingMediaType(lineData.mediaType || 'image');
      setEditingMediaUrl(lineData.mediaUrl || '');
      setEditingThumbnailUrl(lineData.thumbnailUrl || '');
      setEditingSeverity(lineData.severity || 'info');
      setIsEditing(true);
    }
  };

  // Handle saving edit
  const handleSaveEdit = () => {
    if (onEdit) {
      const updates: any = {};
      
      if (editingLabel.trim() !== lineData?.label) {
        updates.label = editingLabel.trim();
      }
      if (editingDescription.trim() !== lineData?.description) {
        updates.description = editingDescription.trim();
      }
      if (editingMediaUrl.trim() !== lineData?.mediaUrl) {
        updates.mediaUrl = editingMediaUrl.trim();
      }
      if (editingThumbnailUrl.trim() !== lineData?.thumbnailUrl) {
        updates.thumbnailUrl = editingThumbnailUrl.trim();
      }
      if (editingMediaType !== lineData?.mediaType) {
        updates.mediaType = editingMediaType;
      }
      if (editingSeverity !== lineData?.severity) {
        updates.severity = editingSeverity;
      }
      
      if (Object.keys(updates).length > 0) {
        // Add edit tracking
        updates.editedAt = new Date().toISOString();
        updates.editedBy = user?.email || 'Unknown User';
        onEdit(updates);
      }
    }
    setIsEditing(false);
    onClose();
  };

  // Handle canceling edit
  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditingLabel('');
    setEditingDescription('');
    setEditingMediaUrl('');
    setEditingThumbnailUrl('');
    setEditingSeverity('info');
  };

  // Handle key presses in input
  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSaveEdit();
    } else if (e.key === 'Escape') {
      handleCancelEdit();
    }
  };

  // Click outside handler to close menu
  useEffect(() => {
    if (!open) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    // Add delay to prevent immediate closing from the same click that opened it
    setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
    }, 100);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      ref={menuRef}
      style={{
        position: 'fixed',
        left: position?.x || 0,
        top: position?.y || 0,
        zIndex: 99999, // Increased z-index to ensure it appears above everything
      }}
    >
      <div className={`bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 p-2 ${isEditing ? 'min-w-[320px]' : 'min-w-[200px]'}`}>
        {/* Header with line info */}
        {lineData && (
          <div className="px-2 py-1 mb-2 border-b border-gray-200 dark:border-gray-600">
            {isEditing ? (
              <div className="space-y-3">
                {/* Label */}
                <div>
                  <label className="text-xs text-gray-500 block mb-1">Label</label>
                  <Input
                    ref={inputRef}
                    value={editingLabel}
                    onChange={(e) => setEditingLabel(e.target.value)}
                    className="text-sm h-8 px-2"
                    placeholder="Enter label"
                  />
                </div>

                {/* Description */}
                <div>
                  <Textarea
                    label="Description"
                    value={editingDescription}
                    onChange={(e) => setEditingDescription(e.target.value)}
                    placeholder="Enter description"
                    grow
                    variant="outlined"
                    rows={3}
                  />
                </div>

                {/* Severity */}
                <div>
                  <label className="text-xs text-gray-500 block mb-1">Severity</label>
                  <Select value={editingSeverity} onValueChange={(value: 'info' | 'low' | 'medium' | 'high' | 'critical') => setEditingSeverity(value)}>
                    <SelectTrigger className="h-8 text-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="info">
                        <div className="flex items-center gap-2">
                          <Info className="w-4 h-4 text-blue-500" />
                          Info
                        </div>
                      </SelectItem>
                      <SelectItem value="low">
                        <div className="flex items-center gap-2">
                          <AlertCircle className="w-4 h-4 text-green-500" />
                          Low
                        </div>
                      </SelectItem>
                      <SelectItem value="medium">
                        <div className="flex items-center gap-2">
                          <AlertTriangle className="w-4 h-4 text-yellow-500" />
                          Medium
                        </div>
                      </SelectItem>
                      <SelectItem value="high">
                        <div className="flex items-center gap-2">
                          <AlertTriangle className="w-4 h-4 text-orange-500" />
                          High
                        </div>
                      </SelectItem>
                      <SelectItem value="critical">
                        <div className="flex items-center gap-2">
                          <Zap className="w-4 h-4 text-red-500" />
                          Critical
                        </div>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Media Type */}
                <div>
                  <label className="text-xs text-gray-500 block mb-1">Media Type</label>
                  <Select value={editingMediaType} onValueChange={(value: 'image' | 'video') => setEditingMediaType(value)}>
                    <SelectTrigger className="h-8 text-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="image">
                        <div className="flex items-center gap-2">
                          <Image className="w-4 h-4" />
                          Image
                        </div>
                      </SelectItem>
                      <SelectItem value="video">
                        <div className="flex items-center gap-2">
                          <Video className="w-4 h-4" />
                          Video
                        </div>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Media URL */}
                <div>
                  <label className="text-xs text-gray-500 block mb-1">
                    {editingMediaType === 'video' ? 'Video URL' : 'Image URL'}
                  </label>
                  <Input
                    value={editingMediaUrl}
                    onChange={(e) => setEditingMediaUrl(e.target.value)}
                    className="text-sm h-8 px-2"
                    placeholder={`Enter ${editingMediaType} URL`}
                  />
                </div>

                {/* Thumbnail URL (for videos) */}
                {editingMediaType === 'video' && (
                  <div>
                    <label className="text-xs text-gray-500 block mb-1">Thumbnail URL</label>
                    <Input
                      value={editingThumbnailUrl}
                      onChange={(e) => setEditingThumbnailUrl(e.target.value)}
                      className="text-sm h-8 px-2"
                      placeholder="Enter thumbnail URL"
                    />
                  </div>
                )}

                {/* Save/Cancel buttons */}
                <div className="flex items-center gap-1 pt-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="flex-1 h-8 text-green-600 hover:bg-green-50"
                    onClick={handleSaveEdit}
                  >
                    <Check className="w-3 h-3 mr-1" />
                    Save
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="flex-1 h-8 text-red-600 hover:bg-red-50"
                    onClick={handleCancelEdit}
                  >
                    <X className="w-3 h-3 mr-1" />
                    Cancel
                  </Button>
                </div>
              </div>
            ) : (
              <div className="text-sm font-medium">{lineData.label}</div>
            )}
            {/* Show source (system/username) */}
            {lineData.source && (
              <div className="text-xs text-gray-500 flex items-center gap-1 mt-1">
                {lineData.source === 'system' ? (
                  <>
                    <Database className="w-3 h-3" />
                    System
                  </>
                ) : (
                  <>
                    <Users className="w-3 h-3" />
                    {lineData.username || 'User'}
                  </>
                )}
              </div>
            )}
            {/* Show date added */}
            {lineData.timestamp && (
              <div className="text-xs text-gray-500 flex items-center gap-1 mt-1">
                <Calendar className="w-3 h-3" />
                <span className="text-xs">
                  {new Date(lineData.timestamp).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </span>
              </div>
            )}
          </div>
        )}

        <div className="flex flex-col gap-0.5">
          {/* Focus/Unfocus Group option */}
          <Button
            variant="ghost"
            size="sm"
            className={`w-full justify-start gap-2 hover:bg-gray-100 dark:hover:bg-gray-700 ${
              isFocused ? 'bg-blue-50 dark:bg-blue-900/20' : ''
            }`}
            onClick={() => {
              if (isFocused && onUnfocusGroup) {
                onUnfocusGroup();
              } else {
                onFocusGroup();
              }
              onClose();
            }}
          >
            <Focus className="w-4 h-4" />
            {isFocused ? 'Unfocus Group' : 'Focus Group'}
          </Button>

          {/* Save to Database option */}
          <Button
            variant="ghost"
            size="sm"
            className="w-full justify-start gap-2 hover:bg-gray-100 dark:hover:bg-gray-700"
            onClick={() => {
              onSaveToDatabase();
              onClose();
            }}
          >
            <Database className="w-4 h-4" />
            Save to Database
          </Button>

          {/* Edit option */}
          {onEdit && !isEditing && (
            <Button
              variant="ghost"
              size="sm"
              className="w-full justify-start gap-2 hover:bg-gray-100 dark:hover:bg-gray-700"
              onClick={handleStartEdit}
            >
              <Edit className="w-4 h-4" />
              Edit Details
            </Button>
          )}

          {/* Separator before Dim/Remove */}
          {(onDim || onDimGroup || (onRemove && canRemove)) && (
            <div className="border-t border-gray-200 dark:border-gray-600 my-1" />
          )}

          {/* Dim single line option */}
          {onDim && (
            <Button
              variant="ghost"
              size="sm"
              className={`w-full justify-start gap-2 hover:bg-gray-100 dark:hover:bg-gray-700 ${
                lineData?.dimmed ? 'text-primary-600 dark:text-primary-400' : ''
              }`}
              onClick={() => {
                onDim();
                onClose();
              }}
            >
              {lineData?.dimmed ? (
                <>
                  <Eye className="w-4 h-4" />
                  Undim
                </>
              ) : (
                <>
                  <EyeOff className="w-4 h-4" />
                  Dim
                </>
              )}
            </Button>
          )}

          {/* Dim entire group option */}
          {onDimGroup && (
            <Button
              variant="ghost"
              size="sm"
              className={`w-full justify-start gap-2 hover:bg-gray-100 dark:hover:bg-gray-700 ${
                lineData?.groupDimmed ? 'text-primary-600 dark:text-primary-400' : ''
              }`}
              onClick={() => {
                onDimGroup();
                onClose();
              }}
            >
              {lineData?.groupDimmed ? (
                <>
                  <Eye className="w-4 h-4" />
                  Undim Group
                </>
              ) : (
                <>
                  <EyeOff className="w-4 h-4" />
                  Dim Group
                </>
              )}
            </Button>
          )}

          {/* Remove option - only when canRemove is true */}
          {onRemove && canRemove && (
            <Button
              variant="ghost"
              size="sm"
              className="w-full justify-start gap-2 hover:bg-red-50 dark:hover:bg-red-900/20 text-red-600 dark:text-red-400"
              onClick={() => {
                onRemove();
                onClose();
              }}
            >
              <Trash2 className="w-4 h-4" />
              Remove
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};